/**
 * Customer-facing layout component with Tesla-style minimal navbar
 */

import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  useTheme,
  useMediaQuery,
  Container,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Close as CloseIcon,
  LightMode,
  DarkMode,
  DirectionsCar,
} from '@mui/icons-material';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import ScrollToTop from '../ScrollToTop';

interface CustomerLayoutProps {
  darkMode?: boolean;
  onToggleDarkMode?: () => void;
}

const CustomerLayout: React.FC<CustomerLayoutProps> = ({
  darkMode = false,
  onToggleDarkMode,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const isHomePage = location.pathname === '/public';
  const isTransparent = isHomePage && !mobileMenuOpen;

  const navigationItems = [
    { label: 'Home', path: '/public' },
    { label: 'Demo', path: '/demo' },
  ];

  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleNavigate = (path: string) => {
    navigate(path);
    setMobileMenuOpen(false);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <ScrollToTop />
      {/* Navigation Bar */}
      <AppBar
        position="fixed"
        sx={{
          backgroundColor: darkMode
            ? 'rgba(0, 0, 0, 0.9)'
            : 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          borderBottom: `1px solid ${
            darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'
          }`,
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          color: darkMode ? '#ffffff' : '#000000',
          transition: 'all 0.3s ease',
        }}
      >
        <Toolbar sx={{ minHeight: '80px', px: { xs: 2, md: 4 } }}>
          {/* Logo */}
          <Box
            component={Link}
            to="/public"
            sx={{
              display: 'flex',
              alignItems: 'center',
              textDecoration: 'none',
              color: 'inherit',
              flexGrow: 0,
              mr: 4,
            }}
          >
            <img 
              src="/DealerOS - Icon.svg" 
              alt="DealerOS Icon" 
              style={{ 
                height: '32px', 
                width: '32px',
                marginRight: '8px'
              }} 
            />
            <Typography
              variant="h5"
              component="div"
              sx={{
                fontWeight: 700,
                letterSpacing: '-0.02em',
                display: { xs: 'none', sm: 'block' },
                color: 'inherit'
              }}
            >
              Dealer<Box component="span" sx={{ fontWeight: 800 }}>OS</Box>
            </Typography>
          </Box>

          {/* Desktop Navigation */}
          {!isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, ml: 4 }}>
              {navigationItems.map((item) => (
                <Button
                  key={item.path}
                  component={Link}
                  to={item.path}
                  sx={{
                    color: 'inherit',
                    fontSize: '1rem',
                    fontWeight: 500,
                    mx: 1,
                    px: 2,
                    py: 1,
                    borderRadius: '8px',
                    transition: 'all 0.3s ease',
                    backgroundColor: location.pathname === item.path
                      ? 'rgba(255, 255, 255, 0.1)'
                      : 'transparent',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      transform: 'translateY(-1px)',
                    },
                  }}
                >
                  {item.label}
                </Button>
              ))}
            </Box>
          )}

          <Box sx={{ flexGrow: 1 }} />

          {/* Dark Mode Toggle */}
          <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
            <IconButton
              onClick={onToggleDarkMode}
              sx={{
                color: 'inherit',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              {darkMode ? <LightMode /> : <DarkMode />}
            </IconButton>
          </Box>

          {/* Mobile Menu Button */}
          {isMobile && (
            <IconButton
              edge="end"
              color="inherit"
              onClick={handleMobileMenuToggle}
              sx={{
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              {mobileMenuOpen ? <CloseIcon /> : <MenuIcon />}
            </IconButton>
          )}
        </Toolbar>
      </AppBar>

      {/* Mobile Navigation Drawer */}
      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        sx={{
          '& .MuiDrawer-paper': {
            width: 280,
            backgroundColor: darkMode ? '#111111' : '#ffffff',
            color: darkMode ? '#ffffff' : '#000000',
            pt: 10,
          },
        }}
      >
        <List>
          {navigationItems.map((item) => (
            <ListItem
              key={item.path}
              onClick={() => handleNavigate(item.path)}
              sx={{
                cursor: 'pointer',
                borderRadius: '8px',
                mx: 2,
                my: 0.5,
                backgroundColor: location.pathname === item.path
                  ? darkMode
                    ? 'rgba(255, 255, 255, 0.1)'
                    : 'rgba(0, 0, 0, 0.05)'
                  : 'transparent',
                '&:hover': {
                  backgroundColor: darkMode
                    ? 'rgba(255, 255, 255, 0.05)'
                    : 'rgba(0, 0, 0, 0.05)',
                },
              }}
            >
              <ListItemText
                primary={item.label}
                sx={{
                  '& .MuiTypography-root': {
                    fontSize: '1.1rem',
                    fontWeight: 500,
                  },
                }}
              />
            </ListItem>
          ))}
        </List>
      </Drawer>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          backgroundColor: darkMode ? '#000000' : '#ffffff',
          color: darkMode ? '#ffffff' : '#000000',
          minHeight: '100vh',
        }}
      >
        <Outlet />
      </Box>

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          backgroundColor: darkMode ? '#111111' : '#f8f9fa',
          borderTop: `1px solid ${
            darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'
          }`,
          py: 6,
          mt: 8,
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              justifyContent: 'space-between',
              alignItems: { xs: 'center', md: 'flex-start' },
              gap: 4,
            }}
          >
            {/* Logo and Description */}
            <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <img 
                  src="/DealerOS - Icon.svg" 
                  alt="DealerOS Icon" 
                  style={{ 
                    height: '28px', 
                    width: '28px',
                    marginRight: '8px'
                  }} 
                />
                <Typography variant="h6" sx={{ fontWeight: 700, color: darkMode ? 'white' : '#000000' }}>
                  Dealer<Box component="span" sx={{ fontWeight: 800 }}>OS</Box>
                </Typography>
              </Box>
              <Typography
                variant="body2"
                color="textSecondary"
                sx={{ maxWidth: 300 }}
              >
                Find your perfect vehicle with our modern, customer-first approach
                to car shopping.
              </Typography>
            </Box>

            {/* Quick Links */}
            <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: darkMode ? 'white' : 'text.primary' }}>
                Quick Links
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {navigationItems.map((item) => (
                  <Button
                    key={item.path}
                    component={Link}
                    to={item.path}
                    sx={{
                      color: 'textSecondary',
                      textAlign: 'left',
                      justifyContent: 'flex-start',
                      p: 0,
                      '&:hover': {
                        color: 'primary.main',
                        backgroundColor: 'transparent',
                      },
                    }}
                  >
                    {item.label}
                  </Button>
                ))}
              </Box>
            </Box>

            {/* Contact Info */}
            <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: darkMode ? 'white' : 'text.primary' }}>
                Contact
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                📧 info@hotspotautos.com.au
              </Typography>
              <Typography variant="body2" color="textSecondary">
                📍 42 Parramatta Road, Granville NSW 2142
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              borderTop: `1px solid ${
                darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'
              }`,
              pt: 3,
              mt: 4,
              textAlign: 'center',
            }}
          >
            <Typography variant="body2" color="textSecondary">
              © 2025 DealerOS. All rights reserved. | Privacy Policy | Terms of Service
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default CustomerLayout;